/**
 * Class Viktor
 *
 * @class Viktor
 */

// * IMPORTS
import Bugsnag from '@bugsnag/js';
import axios from 'axios';
import queryString from 'query-string';

import { HttpStatus } from '@/enums/httpStatus';
import { getShouldNotifyBugSnag } from '@/helpers/reporter';
import { encodeAfURIParams } from '@/helpers/uri';
import { useAuthStore } from '@/stores/auth';
import { useGlobalStore } from '@/stores/global';

// * CLASS
export default class Viktor {
  // * MEMBERS
  /**
   * Init members of client
   */
  // string
  private viktorURL: string;
  // AxiosInstance
  public axiosInstance;

  // * GETTERS

  /**
   * Get Viktor URL
   *
   * @returns {string} The URL
   */
  get getViktorURL() {
    return this.viktorURL;
  }

  // * CONSTRUCTOR
  /**
   * Construct class viktor client
   */
  constructor() {
    // Get Viktor URL
    this.viktorURL = useGlobalStore().getAPIURL;

    // Init axios instance
    if (this.viktorURL) {
      this.axiosInstance = axios.create({
        baseURL: this.viktorURL,
        withCredentials: true,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        paramsSerializer: {
          serialize: params => {
            return queryString.stringify(encodeAfURIParams(params), { arrayFormat: 'comma', encode: false });
          },
          indexes: null
        }
      });
    }

    // * INTERCEPTORS
    // Init interceptor for viktor instance
    if (this.axiosInstance) {
      // Request interceptor
      this.axiosInstance.interceptors.request.use(
        config => {
          return config;
        },
        error => {
          return Promise.reject(error);
        }
      );

      // Response interceptor
      this.axiosInstance.interceptors.response.use(
        response => {
          // Any status code that lie within the range of 2xx cause this function to trigger
          if (response.headers['content-disposition']) {
            return { data: response.data, 'content-disposition': response.headers['content-disposition'] };
          }
          return response.data;
        },
        error => {
          // Any status codes that falls outside the range of 2xx cause this function to trigger
          if (
            error.response?.status === HttpStatus.UNAUTHORIZED ||
            error.response?.status === HttpStatus.SERVICE_UNAVAILABLE
          ) {
            // Deep-link a logged-out user coming from an SSO organisation to its own SSO login page.
            // The new `authRedirect` contract is authoritative: a slug means SSO, `af` (or absent)
            // means the standard AssessFirst login. When absent, fall back to the legacy `sso` param.
            const params = new URLSearchParams(window.location.search);
            const authRedirect = params.get('authRedirect');
            const ssoSlug = authRedirect !== null ? (authRedirect !== 'af' ? authRedirect : null) : params.get('sso');
            if (ssoSlug) {
              window.location.assign(`${useGlobalStore().root_url}/sso/${ssoSlug}/login`);
            } else {
              // Handle 401 error
              const loginRedirectUrl = `${useGlobalStore().auth_url}/?forceRedirect=${window.location.href}`;

              window.location.assign(loginRedirectUrl);
            }
          } else {
            if (getShouldNotifyBugSnag(error.response)) {
              const currentUser = useAuthStore().getFullUser;

              Bugsnag.notify(error, event => {
                event.addMetadata('errorResponse', error.response?.data);
                event.addMetadata('request', {
                  payload: error.config?.data
                });
                event.addMetadata('user', {
                  uuid: currentUser?.uuid,
                  email: currentUser?.email
                });
              });
            }
          }

          return Promise.reject(error);
        }
      );
    }
  }
}
